import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Baby, Plus, Pencil, Trash2, AlertTriangle, Phone, User, Calendar, Building2, Heart, X, FileText, ClipboardCheck, DollarSign, Search, UserCheck, Users, Briefcase, CheckCircle, Circle, ExternalLink, Clock, Camera, ArrowRightLeft
} from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/context/PermissionsContext';
import { toast } from 'sonner';
import ChildRecordsForms from '@/components/ChildRecordsForms';
import ChildBillingSettings from '@/components/ChildBillingSettings';
import DocumentUploader from '@/components/DocumentUploader';

const SHIFT_OPTIONS_FALLBACK = [
  { value: 'Full Day', label: 'Full Day' },
];

// Helper to format time from 24h to 12h display
const formatTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h);
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const hr12 = hr === 0 ? 12 : hr > 12 ? hr - 12 : hr;
  return `${hr12}:${m} ${ampm}`;
};

// Form type display names for tooltip - ALL DCF forms
const FORM_NAMES = {
  enrollment: 'Child Care Enrollment',
  health_history: 'Health History',
  immunization: 'Immunization Record',
  physical_exam: 'Physical Exam',
  emergency_info: 'Emergency Info',
  authorization: 'Field Trip Authorization'
};

// Required vs Optional forms
const REQUIRED_FORMS = ['enrollment', 'health_history', 'immunization', 'physical_exam'];

import ChildDevelopmentCommunications from '@/components/ChildDevelopmentCommunications';

import API from '@/config/api';
// API imported from config;

const ChildrenPage = () => {
  const { user, getAuthHeaders } = useAuth();
  const { canEditChildren, isAdmin } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [children, setChildren] = useState([]);
  const [parents, setParents] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [shiftTimes, setShiftTimes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [shiftFilter, setShiftFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    date_of_birth: '',
    gender: '',
    parent_id: '',
    allergies: '',
    medical_notes: '',
    emergency_contact: '',
    emergency_phone: '',
    classroom: 'General',
    usual_shift: 'Full Day',
    first_attendance_date: '',
    is_employee_child: false
  });
  
  // Authorized pickup state
  const [authorizedPickups, setAuthorizedPickups] = useState([]);
  const [showAddPickupDialog, setShowAddPickupDialog] = useState(false);
  const [editingPickup, setEditingPickup] = useState(null);
  const [pickupForm, setPickupForm] = useState({
    name: '',
    relationship: '',
    phone: '',
    notes: ''
  });
  const [activeProfileTab, setActiveProfileTab] = useState('info');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [moveClassroomOpen, setMoveClassroomOpen] = useState(false);
  const [moveTargetClassroom, setMoveTargetClassroom] = useState('');
  const [moveTime, setMoveTime] = useState('');
  const [childMoveHistory, setChildMoveHistory] = useState([]);
  const [editingMoveId, setEditingMoveId] = useState(null);
  const [editMoveTime, setEditMoveTime] = useState('');
  const [editMoveNotes, setEditMoveNotes] = useState('');
  const [editingName, setEditingName] = useState(false);
  const [editFirstName, setEditFirstName] = useState('');
  const [editLastName, setEditLastName] = useState('');
  const [editingHealthDate, setEditingHealthDate] = useState(false);
  const [healthReportDate, setHealthReportDate] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  // Handle highlight parameter from URL
  useEffect(() => {
    const highlightId = searchParams.get('highlight');
    if (highlightId && children.length > 0) {
      const child = children.find(c => c.id === highlightId);
      if (child) {
        setSelectedChild(child);
        setProfileDialogOpen(true);
        fetchAuthorizedPickups(child.id);
        // Clear the highlight param
        setSearchParams({});
      }
    }
  }, [children, searchParams]);

  const fetchData = async () => {
    try {
      const [childrenRes, parentsRes, classroomsRes, shiftTimesRes] = await Promise.all([
        axios.get(`${API}/children`, getAuthHeaders()),
        axios.get(`${API}/users?role=parent`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/classrooms`, getAuthHeaders()).catch(() => ({ data: [] })),
        axios.get(`${API}/settings/shift-times`, getAuthHeaders()).catch(() => ({ data: [] }))
      ]);
      setChildren(childrenRes.data);
      setParents(parentsRes.data);
      setClassrooms(classroomsRes.data);
      setShiftTimes(shiftTimesRes.data || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuthorizedPickups = async (childId) => {
    try {
      const response = await axios.get(`${API}/children/${childId}/authorized-pickups`, getAuthHeaders());
      setAuthorizedPickups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch authorized pickups:', error);
      setAuthorizedPickups([]);
    }
  };

  const handleAddPickup = async () => {
    if (!pickupForm.name || !pickupForm.relationship || !pickupForm.phone) {
      toast.error('Please fill in name, relationship, and phone');
      return;
    }
    
    try {
      if (editingPickup) {
        await axios.put(
          `${API}/children/${selectedChild.id}/authorized-pickups/${editingPickup.id}`,
          pickupForm,
          getAuthHeaders()
        );
        toast.success('Authorized pickup updated');
      } else {
        await axios.post(
          `${API}/children/${selectedChild.id}/authorized-pickups`,
          pickupForm,
          getAuthHeaders()
        );
        toast.success('Authorized pickup added');
      }
      setShowAddPickupDialog(false);
      setEditingPickup(null);
      setPickupForm({ name: '', relationship: '', phone: '', notes: '' });
      fetchAuthorizedPickups(selectedChild.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save authorized pickup');
    }
  };

  const handleDeletePickup = async (pickupId) => {
    if (!window.confirm('Remove this authorized pickup person?')) return;
    
    try {
      await axios.delete(
        `${API}/children/${selectedChild.id}/authorized-pickups/${pickupId}`,
        getAuthHeaders()
      );
      toast.success('Authorized pickup removed');
      fetchAuthorizedPickups(selectedChild.id);
    } catch (error) {
      toast.error('Failed to remove authorized pickup');
    }
  };

  const openEditPickup = (pickup) => {
    setEditingPickup(pickup);
    setPickupForm({
      name: pickup.name,
      relationship: pickup.relationship,
      phone: pickup.phone,
      notes: pickup.notes || ''
    });
    setShowAddPickupDialog(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const payload = {
      ...formData,
      parent_id: user.role === 'parent' ? user.id : formData.parent_id,
      allergies: formData.allergies.split(',').map(a => a.trim()).filter(Boolean)
    };

    try {
      if (editingChild) {
        await axios.put(`${API}/children/${editingChild.id}`, payload, getAuthHeaders());
        toast.success('Child updated successfully');
      } else {
        await axios.post(`${API}/children`, payload, getAuthHeaders());
        toast.success('Child added successfully');
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleEdit = (child) => {
    setEditingChild(child);
    setFormData({
      first_name: child.first_name,
      last_name: child.last_name,
      date_of_birth: child.date_of_birth,
      gender: child.gender || '',
      parent_id: child.parent_id,
      allergies: (child.allergies || []).join(', '),
      medical_notes: child.medical_notes || '',
      emergency_contact: child.emergency_contact || '',
      emergency_phone: child.emergency_phone || '',
      classroom: child.classroom || 'General',
      usual_shift: child.usual_shift || 'Full Day',
      first_attendance_date: child.first_attendance_date || '',
      is_employee_child: child.is_employee_child || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (childId) => {
    if (!window.confirm('Are you sure you want to remove this child?')) return;
    
    try {
      await axios.delete(`${API}/children/${childId}`, getAuthHeaders());
      toast.success('Child removed successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to remove child');
    }
  };

  const handlePhotoUpload = async (childId, file) => {
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await axios.post(`${API}/children/${childId}/photo`, formData, {
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Photo uploaded successfully');
      fetchData();
      if (selectedChild && selectedChild.id === childId) {
        setSelectedChild(prev => ({ ...prev, photo_url: `/api/children/${childId}/photo?t=${Date.now()}` }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleDeletePhoto = async (childId) => {
    try {
      await axios.delete(`${API}/children/${childId}/photo`, getAuthHeaders());
      toast.success('Photo removed');
      fetchData();
      if (selectedChild && selectedChild.id === childId) {
        setSelectedChild(prev => ({ ...prev, photo_url: null }));
      }
    } catch (error) {
      toast.error('Failed to remove photo');
    }
  };

  const handleMoveClassroom = async () => {
    if (!selectedChild || !moveTargetClassroom) return;
    try {
      const moveTimeISO = moveTime
        ? new Date(`${new Date().toISOString().split('T')[0]}T${moveTime}:00`).toISOString()
        : new Date().toISOString();
      
      await axios.post(`${API}/children/${selectedChild.id}/moves`, {
        to_classroom: moveTargetClassroom,
        move_time: moveTimeISO
      }, getAuthHeaders());
      toast.success(`Moved ${selectedChild.first_name} to ${moveTargetClassroom}`);
      setMoveClassroomOpen(false);
      setMoveTargetClassroom('');
      setMoveTime('');
      fetchData();
      setSelectedChild(prev => ({ ...prev, classroom: moveTargetClassroom }));
      fetchChildMoveHistory(selectedChild.id);
    } catch (error) {
      toast.error('Failed to move child');
    }
  };

  const fetchChildMoveHistory = async (childId) => {
    try {
      const res = await axios.get(`${API}/children/${childId}/moves`, getAuthHeaders());
      setChildMoveHistory(res.data || []);
    } catch (error) {
      setChildMoveHistory([]);
    }
  };

  const handleEditMoveRecord = async (childId, moveId) => {
    try {
      const updates = {};
      if (editMoveTime) {
        updates.move_time = new Date(`${new Date().toISOString().split('T')[0]}T${editMoveTime}:00`).toISOString();
      }
      if (editMoveNotes !== undefined) updates.notes = editMoveNotes;
      
      await axios.put(`${API}/children/${childId}/moves/${moveId}`, updates, getAuthHeaders());
      toast.success('Move record updated');
      setEditingMoveId(null);
      fetchChildMoveHistory(childId);
    } catch (error) {
      toast.error('Failed to update move');
    }
  };

  const handleSaveName = async () => {
    if (!selectedChild || (!editFirstName.trim() && !editLastName.trim())) return;
    try {
      await axios.put(`${API}/children/${selectedChild.id}`, {
        first_name: editFirstName.trim(),
        last_name: editLastName.trim()
      }, getAuthHeaders());
      toast.success('Name updated');
      setEditingName(false);
      setSelectedChild(prev => ({ ...prev, first_name: editFirstName.trim(), last_name: editLastName.trim() }));
      fetchData();
    } catch (error) {
      toast.error('Failed to update name');
    }
  };

  const handleSaveHealthReportDate = async () => {
    if (!selectedChild || !healthReportDate) return;
    try {
      await axios.put(`${API}/children/${selectedChild.id}`, {
        last_health_report_date: healthReportDate
      }, getAuthHeaders());
      toast.success('Health report date saved');
      setEditingHealthDate(false);
      setSelectedChild(prev => ({ ...prev, last_health_report_date: healthReportDate }));
      fetchData();
    } catch (error) {
      toast.error('Failed to save date');
    }
  };

  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      date_of_birth: '',
      parent_id: '',
      allergies: '',
      medical_notes: '',
      emergency_contact: '',
      emergency_phone: '',
      classroom: 'General',
      first_attendance_date: '',
      is_employee_child: false
    });
    setEditingChild(null);
  };

  if (loading) {
    return (
      <DashboardLayout title="Children">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Children">
      <div className="space-y-6" data-testid="children-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <p className="text-muted-foreground">Manage enrolled children and their information</p>
          <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
            {/* Search Bar */}
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search children..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
                data-testid="children-search-input"
              />
            </div>
            {/* Shift Filter */}
            <Select value={shiftFilter} onValueChange={setShiftFilter}>
              <SelectTrigger className="w-full sm:w-44" data-testid="children-shift-filter">
                <Clock className="w-4 h-4 mr-2 text-muted-foreground" />
                <SelectValue placeholder="Filter by shift" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Shifts</SelectItem>
                {shiftTimes.map((s) => (
                  <SelectItem key={s.id} value={s.name}>{s.name} ({formatTime(s.start_time)}–{formatTime(s.end_time)})</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canEditChildren() && (
              <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
                <DialogTrigger asChild>
                  <Button className="btn-primary" data-testid="add-child-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Child
                  </Button>
                </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">
                  {editingChild ? 'Edit Child' : 'Add New Child'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name">First Name</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                      required
                      data-testid="child-first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name">Last Name</Label>
                    <Input
                      id="last_name"
                      value={formData.last_name}
                      onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                      required
                      data-testid="child-last-name-input"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="date_of_birth">Date of Birth</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                      required
                      data-testid="child-dob-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="gender">Gender</Label>
                    <Select 
                      value={formData.gender} 
                      onValueChange={(value) => setFormData({...formData, gender: value})}
                    >
                      <SelectTrigger data-testid="child-gender-select">
                        <SelectValue placeholder="Select gender" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Male">Male</SelectItem>
                        <SelectItem value="Female">Female</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_attendance_date">First Day of Attendance</Label>
                    <Input
                      id="first_attendance_date"
                      type="date"
                      value={formData.first_attendance_date}
                      onChange={(e) => setFormData({...formData, first_attendance_date: e.target.value})}
                      data-testid="child-first-attendance-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="classroom">Classroom</Label>
                  <Select 
                    value={formData.classroom} 
                    onValueChange={(value) => setFormData({...formData, classroom: value})}
                  >
                    <SelectTrigger data-testid="child-classroom-select">
                      <SelectValue placeholder="Select classroom" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="General">General</SelectItem>
                      {classrooms.map((classroom) => (
                        <SelectItem key={classroom.id} value={classroom.name}>
                          {classroom.name} {classroom.age_range && `(${classroom.age_range})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="usual_shift">Usual Shift</Label>
                  <Select 
                    value={formData.usual_shift} 
                    onValueChange={(value) => setFormData({...formData, usual_shift: value})}
                  >
                    <SelectTrigger data-testid="child-shift-select">
                      <SelectValue placeholder="Select shift" />
                    </SelectTrigger>
                    <SelectContent>
                      {shiftTimes.map((s) => (
                        <SelectItem key={s.id} value={s.name}>{s.name} ({formatTime(s.start_time)}–{formatTime(s.end_time)})</SelectItem>
                      ))}
                      {shiftTimes.length === 0 && <SelectItem value="Full Day">Full Day</SelectItem>}
                    </SelectContent>
                  </Select>
                </div>

                {user.role !== 'parent' && (
                  <div className="space-y-2">
                    <Label htmlFor="parent_id">Parent/Guardian</Label>
                    <Select 
                      value={formData.parent_id} 
                      onValueChange={(value) => setFormData({...formData, parent_id: value})}
                    >
                      <SelectTrigger data-testid="child-parent-select">
                        <SelectValue placeholder="Select parent" />
                      </SelectTrigger>
                      <SelectContent>
                        {parents.map((parent) => (
                          <SelectItem key={parent.id} value={parent.id}>
                            {parent.name} ({parent.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Employee's Child Checkbox - DCF Section 16.5 Compliance */}
                {user.role === 'admin' && (
                  <div className="flex items-center space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                    <Checkbox 
                      id="is_employee_child"
                      checked={formData.is_employee_child}
                      onCheckedChange={(checked) => setFormData({...formData, is_employee_child: checked})}
                      data-testid="employee-child-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="is_employee_child" className="font-medium cursor-pointer flex items-center gap-2">
                        <Briefcase className="w-4 h-4 text-amber-600" />
                        Employee's Child
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Mark if this child's parent is an employee (DCF Section 16.5 - max 40% limit)
                      </p>
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="allergies">Allergies (comma separated)</Label>
                  <Input
                    id="allergies"
                    value={formData.allergies}
                    onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                    placeholder="e.g., Peanuts, Dairy, Gluten"
                    data-testid="child-allergies-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="medical_notes">Medical Notes</Label>
                  <Textarea
                    id="medical_notes"
                    value={formData.medical_notes}
                    onChange={(e) => setFormData({...formData, medical_notes: e.target.value})}
                    placeholder="Any medical conditions or special needs..."
                    data-testid="child-medical-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emergency_contact">Emergency Contact Name</Label>
                    <Input
                      id="emergency_contact"
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                      data-testid="child-emergency-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emergency_phone">Emergency Phone</Label>
                    <Input
                      id="emergency_phone"
                      type="tel"
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                      data-testid="child-emergency-phone-input"
                    />
                  </div>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-child-btn">
                    {editingChild ? 'Update Child' : 'Add Child'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
            )}
          </div>
        </div>

        {/* Children Grid - Sorted Alphabetically */}
        {(() => {
          // Filter children based on search query
          const filteredChildren = children.filter(child => {
            // Shift filter
            if (shiftFilter !== 'all' && (child.usual_shift || 'Full Day') !== shiftFilter) return false;
            if (!searchQuery.trim()) return true;
            const query = searchQuery.toLowerCase();
            const fullName = `${child.first_name} ${child.last_name}`.toLowerCase();
            const classroom = (child.classroom || child.current_classroom || '').toLowerCase();
            return fullName.includes(query) || classroom.includes(query);
          });
          
          return filteredChildren.length > 0 ? (
          <div className="space-y-6">
            {/* Group children by first letter of last name */}
            {Object.entries(
              [...filteredChildren]
                .sort((a, b) => {
                  const nameA = `${a.last_name} ${a.first_name}`.toLowerCase();
                  const nameB = `${b.last_name} ${b.first_name}`.toLowerCase();
                  return nameA.localeCompare(nameB);
                })
                .reduce((groups, child) => {
                  const letter = child.last_name.charAt(0).toUpperCase();
                  if (!groups[letter]) groups[letter] = [];
                  groups[letter].push(child);
                  return groups;
                }, {})
            )
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([letter, letterChildren]) => (
              <div key={letter} className="space-y-3">
                {/* Letter Header */}
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-lg">
                    {letter}
                  </div>
                  <span className="text-sm text-muted-foreground">
                    {letterChildren.length} {letterChildren.length === 1 ? 'child' : 'children'}
                  </span>
                </div>
                
                {/* Children Cards for this letter */}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 ml-2">
                  {letterChildren.map((child) => (
                    <Card 
                      key={child.id} 
                      className="card-clay cursor-pointer hover:shadow-lg transition-shadow" 
                      data-testid={`child-card-${child.id}`}
                      onClick={() => { 
                        setSelectedChild(child); 
                        setActiveProfileTab('info');
                        setProfileDialogOpen(true); 
                        fetchAuthorizedPickups(child.id);
                        fetchChildMoveHistory(child.id);
                      }}
                    >
                      <CardContent className="p-5">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            {child.photo_url ? (
                              <img 
                                src={`${API}${child.photo_url}`.replace('/api/api/', '/api/')} 
                                alt={`${child.first_name} ${child.last_name}`}
                                className="w-12 h-12 rounded-full object-cover"
                                data-testid={`child-photo-${child.id}`}
                              />
                            ) : (
                              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                                <span className="font-bold text-lg text-primary">
                                  {child.first_name.charAt(0)}{child.last_name.charAt(0)}
                                </span>
                              </div>
                            )}
                            <div>
                              <h3 className="font-semibold">{child.last_name}, {child.first_name}</h3>
                              <p className="text-xs text-muted-foreground">{child.classroom}</p>
                              {child.usual_shift && (
                                <Badge variant="outline" className="text-[10px] mt-0.5 px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                                  <Clock className="w-2.5 h-2.5 mr-0.5" />
                                  {child.usual_shift}
                                  {(() => { const st = shiftTimes.find(s => s.name === child.usual_shift); return st ? ` ${formatTime(st.start_time)}–${formatTime(st.end_time)}` : ''; })()}
                                </Badge>
                              )}
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            {child.is_employee_child && (
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                                <Briefcase className="w-3 h-3 mr-1" />
                                Employee Child
                              </Badge>
                            )}
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              child.is_checked_in 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-gray-100 text-gray-600'
                            }`}>
                              {child.is_checked_in ? 'Present' : 'Not In'}
                            </span>
                          </div>
                        </div>

                        <div className="space-y-2 text-sm mb-3">
                          <div className="flex items-center gap-2 text-muted-foreground">
                            <Baby className="w-4 h-4" />
                            <span>DOB: {child.date_of_birth}</span>
                          </div>
                          {child.parent_name && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <User className="w-4 h-4" />
                              <span>Parent: {child.parent_name}</span>
                            </div>
                          )}
                          {child.emergency_phone && (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Phone className="w-4 h-4" />
                              <span>{child.emergency_phone}</span>
                            </div>
                          )}
                          {child.allergies && child.allergies.length > 0 && (
                            <div className="flex items-start gap-2">
                              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5" />
                              <div className="flex flex-wrap gap-1">
                                {child.allergies.map((allergy, idx) => (
                                  <span key={idx} className="bg-amber-100 text-amber-800 px-2 py-0.5 rounded text-xs">
                                    {allergy}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>

                        {/* DCF Forms Progress Indicator - Enhanced with All Forms */}
                        {child.forms_progress && (
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div 
                                  className={`p-2 rounded-lg mb-3 cursor-pointer hover:ring-2 hover:ring-primary/50 transition-all ${
                                    child.forms_progress.percentage === 100 
                                      ? 'bg-green-50 border border-green-200' 
                                      : child.forms_progress.percentage >= 50 
                                        ? 'bg-amber-50 border border-amber-200'
                                        : 'bg-red-50 border border-red-200'
                                  }`}
                                  data-testid={`forms-progress-${child.id}`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedChild(child);
                                    setActiveProfileTab('forms');
                                    setProfileDialogOpen(true);
                                  }}
                                >
                                  <div className="flex items-center justify-between mb-1">
                                    <span className="text-xs font-medium flex items-center gap-1">
                                      <ClipboardCheck className="w-3 h-3" />
                                      DCF Forms
                                    </span>
                                    <span className={`text-xs font-semibold ${
                                      child.forms_progress.percentage === 100 
                                        ? 'text-green-600' 
                                        : child.forms_progress.percentage >= 50 
                                          ? 'text-amber-600'
                                          : 'text-red-600'
                                    }`}>
                                      {child.forms_progress.completed}/{child.forms_progress.total}
                                    </span>
                                  </div>
                                  <Progress 
                                    value={child.forms_progress.percentage} 
                                    className={`h-1.5 ${
                                      child.forms_progress.percentage === 100 
                                        ? '[&>div]:bg-green-500' 
                                        : child.forms_progress.percentage >= 50 
                                          ? '[&>div]:bg-amber-500'
                                          : '[&>div]:bg-red-500'
                                    }`}
                                  />
                                  {/* Mini form status icons */}
                                  <div className="flex items-center gap-1 mt-1.5">
                                    {Object.entries(FORM_NAMES).slice(0, 4).map(([key, name]) => {
                                      const status = child.forms_progress.all_forms?.details?.[key];
                                      const isComplete = status?.completed;
                                      return (
                                        <div 
                                          key={key}
                                          className={`w-4 h-4 rounded-full flex items-center justify-center ${
                                            isComplete ? 'bg-green-100' : 'bg-gray-100'
                                          }`}
                                          title={`${name}: ${isComplete ? 'Complete' : 'Incomplete'}`}
                                        >
                                          {isComplete ? (
                                            <CheckCircle className="w-3 h-3 text-green-600" />
                                          ) : (
                                            <Circle className="w-3 h-3 text-gray-400" />
                                          )}
                                        </div>
                                      );
                                    })}
                                    <ExternalLink className="w-3 h-3 text-gray-400 ml-auto" />
                                  </div>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="max-w-xs p-3">
                                <div className="space-y-2">
                                  <p className="font-semibold text-sm border-b pb-1">DCF Forms Status</p>
                                  <div className="grid gap-1">
                                    {Object.entries(FORM_NAMES).map(([key, name]) => {
                                      const status = child.forms_progress.all_forms?.details?.[key];
                                      const isComplete = status?.completed;
                                      const isRequired = REQUIRED_FORMS.includes(key);
                                      return (
                                        <div key={key} className="flex items-center gap-2 text-xs">
                                          {isComplete ? (
                                            <CheckCircle className="w-3 h-3 text-green-500 shrink-0" />
                                          ) : (
                                            <Circle className="w-3 h-3 text-gray-300 shrink-0" />
                                          )}
                                          <span className={isComplete ? 'text-green-700' : 'text-gray-600'}>
                                            {name}
                                          </span>
                                          {isRequired && (
                                            <Badge variant="outline" className="text-[9px] px-1 py-0 h-3">
                                              Required
                                            </Badge>
                                          )}
                                        </div>
                                      );
                                    })}
                                  </div>
                                  <p className="text-[10px] text-muted-foreground pt-1 border-t">
                                    Click to open forms →
                                  </p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        )}

                        {canEditChildren() && (
                          <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
                            <Button 
                              size="sm" 
                              variant="outline" 
                              className="flex-1"
                              onClick={() => handleEdit(child)}
                              data-testid={`edit-child-${child.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            {isAdmin() && (
                              <Button 
                                size="sm" 
                                variant="outline" 
                                className="text-destructive hover:bg-destructive hover:text-white"
                                onClick={() => handleDelete(child.id)}
                                data-testid={`delete-child-${child.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <Card className="card-clay">
            <CardContent className="p-12 text-center">
              <Baby className="w-16 h-16 mx-auto mb-4 text-muted-foreground opacity-50" />
              <h3 className="font-heading font-bold text-xl mb-2">
                {searchQuery ? 'No Children Found' : 'No Children Enrolled'}
              </h3>
              <p className="text-muted-foreground mb-6">
                {searchQuery 
                  ? `No children match "${searchQuery}". Try a different search.`
                  : canEditChildren() 
                    ? 'Get started by adding your first child to the system.'
                    : 'No children are currently enrolled in the system.'}
              </p>
              {!searchQuery && canEditChildren() && (
                <Button onClick={() => setDialogOpen(true)} data-testid="empty-add-child-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Child
                </Button>
              )}
            </CardContent>
          </Card>
        );
        })()}

        {/* Child Profile Dialog */}
        <Dialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Child Profile - {selectedChild?.first_name} {selectedChild?.last_name}
              </DialogTitle>
            </DialogHeader>
            {selectedChild && (
              <Tabs value={activeProfileTab} onValueChange={setActiveProfileTab} className="mt-2">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="info">Profile</TabsTrigger>
                  <TabsTrigger value="forms" data-testid="child-forms-tab">
                    <FileText className="w-4 h-4 mr-1" />
                    DCF Forms
                  </TabsTrigger>
                  <TabsTrigger value="billing" data-testid="child-billing-tab">
                    <DollarSign className="w-4 h-4 mr-1" />
                    Billing
                  </TabsTrigger>
                </TabsList>
                
                <TabsContent value="info" className="mt-4">
                  <div className="space-y-6">
                    {/* Header */}
                    <div className="flex items-center gap-4 p-4 bg-primary/5 rounded-xl">
                      <div className="relative group">
                        {selectedChild.photo_url ? (
                          <img 
                            src={`${API}${selectedChild.photo_url}`.replace('/api/api/', '/api/')} 
                            alt={`${selectedChild.first_name} ${selectedChild.last_name}`}
                            className="w-16 h-16 rounded-full object-cover"
                            data-testid="child-profile-photo"
                          />
                        ) : (
                          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary/20 to-primary/40 flex items-center justify-center">
                            <span className="font-bold text-2xl text-primary">
                              {selectedChild.first_name.charAt(0)}{selectedChild.last_name.charAt(0)}
                            </span>
                          </div>
                        )}
                        {canEditChildren() && (
                          <label className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full opacity-0 group-hover:opacity-100 cursor-pointer transition-opacity" data-testid="upload-photo-trigger">
                            <Camera className="w-5 h-5 text-white" />
                            <input
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => e.target.files[0] && handlePhotoUpload(selectedChild.id, e.target.files[0])}
                              disabled={uploadingPhoto}
                            />
                          </label>
                        )}
                      </div>
                      <div className="flex-1">
                        {editingName ? (
                          <div className="flex items-center gap-2">
                            <Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} placeholder="First" className="h-8 w-28" data-testid="edit-first-name" />
                            <Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} placeholder="Last" className="h-8 w-28" data-testid="edit-last-name" />
                            <Button size="sm" className="h-8" onClick={handleSaveName} data-testid="save-name-btn">Save</Button>
                            <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingName(false)}>Cancel</Button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <h2 className="text-lg font-bold">{selectedChild.first_name} {selectedChild.last_name}</h2>
                            {canEditChildren() && (
                              <Button size="sm" variant="ghost" className="h-6 px-1" onClick={() => { setEditingName(true); setEditFirstName(selectedChild.first_name); setEditLastName(selectedChild.last_name); }} data-testid="edit-name-btn">
                                <Pencil className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={selectedChild.is_checked_in ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                            {selectedChild.is_checked_in ? 'Checked In' : 'Not Checked In'}
                          </Badge>
                          {selectedChild.photo_url && canEditChildren() && (
                            <Button size="sm" variant="ghost" className="text-xs text-red-500 h-6 px-2" onClick={() => handleDeletePhoto(selectedChild.id)} data-testid="delete-photo-btn">
                              Remove Photo
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Assigned Classroom */}
                    <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-blue-600 font-medium mb-1">Assigned Classroom</p>
                          <p className="font-semibold text-blue-800 flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            {selectedChild.classroom || 'Not assigned'}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {selectedChild.is_checked_in && (
                            <Badge className="bg-green-100 text-green-700 border-green-300">
                              Currently Present
                            </Badge>
                          )}
                          {canEditChildren() && (
                            <Button size="sm" variant="outline" onClick={() => { setMoveTargetClassroom(''); setMoveClassroomOpen(true); }} data-testid="move-classroom-btn">
                              <ArrowRightLeft className="w-3.5 h-3.5 mr-1" />
                              Move
                            </Button>
                          )}
                        </div>
                      </div>
                      {moveClassroomOpen && (
                        <div className="mt-3 pt-3 border-t border-blue-200 space-y-2">
                          <div className="flex items-end gap-2">
                            <div className="flex-1">
                              <Label className="text-xs text-blue-600">Move to Classroom</Label>
                              <Select value={moveTargetClassroom} onValueChange={setMoveTargetClassroom}>
                                <SelectTrigger className="mt-1 bg-white" data-testid="move-classroom-select">
                                  <SelectValue placeholder="Select classroom" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="General">General</SelectItem>
                                  {classrooms.map((cr) => (
                                    <SelectItem key={cr.id} value={cr.name}>{cr.name}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <Label className="text-xs text-blue-600">Time</Label>
                              <Input type="time" value={moveTime} onChange={(e) => setMoveTime(e.target.value)} className="mt-1 bg-white w-28" data-testid="move-time-input" />
                            </div>
                            <Button size="sm" onClick={handleMoveClassroom} disabled={!moveTargetClassroom} data-testid="confirm-move-btn">
                              Confirm
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setMoveClassroomOpen(false)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                      {/* Move History */}
                      {childMoveHistory.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-blue-200">
                          <p className="text-xs font-medium text-blue-600 mb-1">Move History</p>
                          <div className="space-y-1">
                            {childMoveHistory.map((mv) => (
                              <div key={mv.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                                {editingMoveId === mv.id ? (
                                  <div className="flex items-center gap-1 flex-wrap">
                                    <Input type="time" value={editMoveTime} onChange={(e) => setEditMoveTime(e.target.value)} className="h-6 text-xs w-24" />
                                    <Input value={editMoveNotes} onChange={(e) => setEditMoveNotes(e.target.value)} placeholder="Notes" className="h-6 text-xs w-28" />
                                    <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => handleEditMoveRecord(selectedChild.id, mv.id)}>Save</Button>
                                    <Button size="sm" variant="ghost" className="h-5 text-xs px-1" onClick={() => setEditingMoveId(null)}>Cancel</Button>
                                  </div>
                                ) : (
                                  <>
                                    <span className="font-medium">{new Date(mv.move_time).toLocaleTimeString([], {hour:'2-digit',minute:'2-digit'})}</span>
                                    <span>{new Date(mv.move_time).toLocaleDateString()}</span>
                                    <span>{mv.from_classroom}</span>
                                    <span>→</span>
                                    <span className="font-medium">{mv.to_classroom}</span>
                                    {mv.notes && <span className="italic">({mv.notes})</span>}
                                    {canEditChildren() && (
                                      <Button size="sm" variant="ghost" className="h-4 px-1" onClick={() => { setEditingMoveId(mv.id); setEditMoveTime(new Date(mv.move_time).toLocaleTimeString('en-GB', {hour:'2-digit',minute:'2-digit'})); setEditMoveNotes(mv.notes || ''); }} data-testid={`edit-move-${mv.id}`}>
                                        <Pencil className="w-2.5 h-2.5" />
                                      </Button>
                                    )}
                                  </>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Details */}
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Date of Birth</p>
                        <p className="font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-primary" />
                          {selectedChild.date_of_birth}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Gender</p>
                        <p className="font-medium">
                          {selectedChild.gender || 'Not set'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">First Day of Attendance</p>
                        <p className="font-medium flex items-center gap-2">
                          <Calendar className="w-4 h-4 text-green-600" />
                          {selectedChild.first_attendance_date || 'Not set'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Parent/Guardian</p>
                        <p className="font-medium flex items-center gap-2">
                          <User className="w-4 h-4 text-primary" />
                          {selectedChild.parent_name || 'Not assigned'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Classroom</p>
                        <p className="font-medium">
                          {selectedChild.classroom || 'Not assigned'}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Usual Shift</p>
                        <p className="font-medium flex items-center gap-2">
                          <Clock className="w-4 h-4 text-blue-600" />
                          {selectedChild.usual_shift || 'Not set'}
                          {(() => { const st = shiftTimes.find(s => s.name === selectedChild.usual_shift); return st ? <span className="text-sm text-muted-foreground">({formatTime(st.start_time)}–{formatTime(st.end_time)})</span> : null; })()}
                        </p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Emergency Contact</p>
                        <p className="font-medium">{selectedChild.emergency_contact || 'Not provided'}</p>
                      </div>
                      <div className="space-y-1">
                        <p className="text-xs text-muted-foreground">Emergency Phone</p>
                        <p className="font-medium flex items-center gap-2">
                          <Phone className="w-4 h-4 text-primary" />
                          {selectedChild.emergency_phone || 'Not provided'}
                        </p>
                      </div>
                    </div>

                    {/* Last Health Report */}
                    <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs text-green-600 font-medium mb-1">Last Child Health Report Date</p>
                          <p className="font-semibold text-green-800 flex items-center gap-2">
                            <Heart className="w-4 h-4" />
                            {selectedChild.last_health_report_date 
                              ? new Date(selectedChild.last_health_report_date + 'T12:00:00').toLocaleDateString()
                              : 'No date on file'}
                          </p>
                          {selectedChild.last_health_report_date && (
                            <p className="text-xs text-green-600 mt-0.5">
                              Next due: {(() => {
                                const d = new Date(selectedChild.last_health_report_date);
                                const dob = selectedChild.date_of_birth ? new Date(selectedChild.date_of_birth) : null;
                                const ageYears = dob ? (new Date() - dob) / (365.25 * 24 * 60 * 60 * 1000) : 3;
                                if (ageYears < 2) d.setMonth(d.getMonth() + 6);
                                else d.setFullYear(d.getFullYear() + 2);
                                return d.toLocaleDateString();
                              })()}
                            </p>
                          )}
                        </div>
                        {canEditChildren() && (
                          editingHealthDate ? (
                            <div className="flex items-center gap-2">
                              <Input type="date" value={healthReportDate} onChange={(e) => setHealthReportDate(e.target.value)} className="h-8 w-36" data-testid="health-report-date-input" />
                              <Button size="sm" className="h-8" onClick={handleSaveHealthReportDate} data-testid="save-health-date-btn">Save</Button>
                              <Button size="sm" variant="ghost" className="h-8" onClick={() => setEditingHealthDate(false)}>Cancel</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" onClick={() => { setEditingHealthDate(true); setHealthReportDate(selectedChild.last_health_report_date || ''); }} data-testid="edit-health-date-btn">
                              <Pencil className="w-3 h-3 mr-1" />
                              {selectedChild.last_health_report_date ? 'Update' : 'Set Date'}
                            </Button>
                          )
                        )}
                      </div>
                    </div>

                    {/* Allergies */}
                    {selectedChild.allergies && selectedChild.allergies.length > 0 && (
                      <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <p className="text-sm font-medium text-amber-800 flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          Allergies
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {selectedChild.allergies.map((allergy, idx) => (
                            <Badge key={idx} className="bg-amber-100 text-amber-800 border-amber-300">
                              {allergy}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Medical Notes */}
                    {selectedChild.medical_notes && (
                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <p className="text-sm font-medium text-blue-800 flex items-center gap-2 mb-2">
                          <Heart className="w-4 h-4" />
                          Medical Notes
                        </p>
                        <p className="text-sm text-blue-700">{selectedChild.medical_notes}</p>
                      </div>
                    )}

                    {/* Document Uploads */}
                    <DocumentUploader entityType="child" entityId={selectedChild.id} entityName={`${selectedChild.first_name} ${selectedChild.last_name}`} />

                    {/* Authorized Pickup Persons */}
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center justify-between mb-3">
                        <p className="text-sm font-medium text-green-800 flex items-center gap-2">
                          <UserCheck className="w-4 h-4" />
                          Authorized Pickup Persons
                        </p>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="h-7 text-green-700 border-green-300 hover:bg-green-100"
                          onClick={() => {
                            setEditingPickup(null);
                            setPickupForm({ name: '', relationship: '', phone: '', notes: '' });
                            setShowAddPickupDialog(true);
                          }}
                        >
                          <Plus className="w-3 h-3 mr-1" />
                          Add Person
                        </Button>
                      </div>
                      
                      {authorizedPickups.length === 0 ? (
                        <p className="text-sm text-green-600 italic">No authorized pickup persons added yet</p>
                      ) : (
                        <div className="space-y-2">
                          {authorizedPickups.map((pickup) => (
                            <div key={pickup.id} className="flex items-center justify-between p-2 bg-white rounded border border-green-200">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium text-sm">{pickup.name}</span>
                                  <Badge variant="outline" className="text-xs">{pickup.relationship}</Badge>
                                </div>
                                <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                                  <Phone className="w-3 h-3" />
                                  {pickup.phone}
                                </p>
                                {pickup.notes && (
                                  <p className="text-xs text-muted-foreground mt-1">{pickup.notes}</p>
                                )}
                              </div>
                              <div className="flex gap-1">
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0"
                                  onClick={() => openEditPickup(pickup)}
                                >
                                  <Pencil className="w-3 h-3" />
                                </Button>
                                <Button 
                                  size="sm" 
                                  variant="ghost" 
                                  className="h-7 w-7 p-0 text-destructive"
                                  onClick={() => handleDeletePickup(pickup.id)}
                                >
                                  <Trash2 className="w-3 h-3" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-3 pt-4 border-t">
                      <Button 
                        variant="outline" 
                        className="flex-1"
                        onClick={() => {
                          setProfileDialogOpen(false);
                          handleEdit(selectedChild);
                        }}
                      >
                        <Pencil className="w-4 h-4 mr-2" />
                        Edit Profile
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => setProfileDialogOpen(false)}
                      >
                        Close
                      </Button>
                    </div>

                    {/* Parent-Staff Development Communications — DCF 251.07(5) */}
                    <ChildDevelopmentCommunications
                      childId={selectedChild.id}
                      childName={`${selectedChild.first_name} ${selectedChild.last_name}`}
                      ageYears={(() => {
                        if (!selectedChild.date_of_birth) return 99;
                        const dob = new Date(selectedChild.date_of_birth);
                        const now = new Date();
                        let y = now.getFullYear() - dob.getFullYear();
                        if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) y--;
                        return y;
                      })()}
                    />
                  </div>
                </TabsContent>
                
                <TabsContent value="forms" className="mt-4">
                  <ChildRecordsForms 
                    childId={selectedChild.id}
                    childName={`${selectedChild.first_name} ${selectedChild.last_name}`}
                    childDob={selectedChild.date_of_birth}
                    onUpdate={fetchData}
                  />
                </TabsContent>

                <TabsContent value="billing" className="mt-4">
                  <ChildBillingSettings
                    childId={selectedChild.id}
                    childName={`${selectedChild.first_name} ${selectedChild.last_name}`}
                    onUpdate={fetchData}
                  />
                </TabsContent>
              </Tabs>
            )}
          </DialogContent>
        </Dialog>

        {/* Add/Edit Authorized Pickup Dialog */}
        <Dialog open={showAddPickupDialog} onOpenChange={setShowAddPickupDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <UserCheck className="w-5 h-5 text-green-600" />
                {editingPickup ? 'Edit Authorized Pickup' : 'Add Authorized Pickup'}
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Full Name *</Label>
                <Input
                  placeholder="e.g., Jane Smith"
                  value={pickupForm.name}
                  onChange={(e) => setPickupForm({...pickupForm, name: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Relationship *</Label>
                <Select
                  value={pickupForm.relationship}
                  onValueChange={(v) => setPickupForm({...pickupForm, relationship: v})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Grandparent">Grandparent</SelectItem>
                    <SelectItem value="Aunt">Aunt</SelectItem>
                    <SelectItem value="Uncle">Uncle</SelectItem>
                    <SelectItem value="Sibling">Sibling (18+)</SelectItem>
                    <SelectItem value="Family Friend">Family Friend</SelectItem>
                    <SelectItem value="Nanny/Babysitter">Nanny/Babysitter</SelectItem>
                    <SelectItem value="Neighbor">Neighbor</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone Number *</Label>
                <Input
                  placeholder="(555) 123-4567"
                  value={pickupForm.phone}
                  onChange={(e) => setPickupForm({...pickupForm, phone: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Notes (Optional)</Label>
                <Input
                  placeholder="e.g., Valid ID required, Call parent first"
                  value={pickupForm.notes}
                  onChange={(e) => setPickupForm({...pickupForm, notes: e.target.value})}
                />
              </div>
              <div className="flex gap-2 pt-4">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => {
                    setShowAddPickupDialog(false);
                    setEditingPickup(null);
                  }}
                >
                  Cancel
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleAddPickup}
                >
                  {editingPickup ? 'Update' : 'Add Person'}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ChildrenPage;
